import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import styles from './TooltipIcon-hashed.scss';
import Tooltip from './Tooltip';
import getIconClasses from '../../iconUtils.js';
import defaultTranslator from '@elixir/translator';
import { withTheme, defaultTheme } from '@elixir/theme';

class TooltipIcon extends React.Component {
    static propTypes = {
        icon: PropTypes.string,
        text: PropTypes.string,
        title: PropTypes.string,
        link: PropTypes.string,
        href: PropTypes.string,
        tipPlacement: PropTypes.string,
        iconPlacement: PropTypes.oneOf(['inside', 'outside'])
    };

    static defaultProps = {
        icon: 'question',
        iconPlacement: 'inside',
        theme: defaultTheme
    };

    /**
     * Define context properties that are set somewhere higher in the component chain.
     * Useful for passing down functions and settings without having to pass down as a prop.
     * Used here to obtain the translator function for localizing strings
     *
     * @type {Object}
     * @property {function(key: string)} translator - The function used to translate strings
     */
    static contextTypes = {
        translator: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);
        this.state = {
            iconClasses: this.getClosedIconClasses(props.icon)
        };

        this.iconRef = React.createRef();
    }
    
    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event) => {
        if (this.iconRef && this.tooltip
            && !this.tooltip.popper.contains(event.target)
            && !this.iconRef.current.contains(event.target)) {
            this.hideTooltip();
        }
    }

    getOpenIconClasses = (icon) => getIconClasses(`${icon}-circle`)

    getClosedIconClasses = (icon) => getIconClasses(`far ${icon}-circle`)

    setClosedIconClasses = async () => this.setState({
        iconClasses: this.getClosedIconClasses(this.props.icon)
    })

    setOpenIconClasses = async () => this.setState({
        iconClasses: this.getOpenIconClasses(this.props.icon)
    })

    toggleTooltip = () => {
        const {
            tooltip,
            setClosedIconClasses,
            setOpenIconClasses
        } = this;

        if (tooltip) {
            tooltip.state.isVisible
                ? setClosedIconClasses().then(tooltip.hide)
                : setOpenIconClasses().then(tooltip.show);
        }

    }

    hideTooltip = () => {
        const {
            tooltip,
            setClosedIconClasses
        } = this;

        if (tooltip && tooltip.state.isVisible) {
            setClosedIconClasses().then(tooltip.hide);
        }

    }

    /**
     * Render tooltip icon
     *
     * @returns {ReactElement} tooltip icon
     */
    render() {
        const {
            text, title, link, href, className, tipPlacement, flipBehavior, theme, iconPlacement
        } = this.props;

        const { iconClasses } = this.state;

        const translator = this.context.translator || defaultTranslator;

        const tooltipComponent = (
            <Fragment>
                { title && <div><strong>{translator(title)}</strong></div>}
                { text && <div>{translator(text)}</div>}
                { link && href && <a href={href} target="_blank">{translator(link)}</a>}
            </Fragment>
        );

        const tooltipProps = {
            content: tooltipComponent,
            placement: tipPlacement,
            trigger: 'manual',
            hideOnClick: false,
            flipBehavior
        };

        const themeStyles = theme.getStyles('TooltipIcon', styles);
        const classes = cx(
            className,
            themeStyles.tooltipIcon,
            {[themeStyles.positionOutside]: iconPlacement === 'outside'}
        );

        return (
            <span className={classes}>
                <Tooltip {...tooltipProps}
                    onCreate={tooltip => { this.tooltip = tooltip; }}
                >
                    <button ref={this.iconRef} type="button" onClick={this.toggleTooltip}>
                        <i className={iconClasses}/>
                    </button>
                </Tooltip>
            </span>
        );
    }
}

export default withTheme(TooltipIcon);
